'use client';

import { styled } from 'styled-components';
import cs from 'classnames';
import { ImageProps, SharedHeroProps, VideoProps } from '@types';
import * as Styled from '@lib/styles';
import Button from '@dvd/components/Button';
import TextLink from '@dvd/components/TextLink';
import {
  HeroDescription,
  HeroLogos,
  HeroComputedMedia,
  HeroMessaging,
  HeroSubtitle,
  HeroMedia,
} from '@dvd/modules/Heroes/HeroSharedComponents';

interface HeroDefaultProps extends SharedHeroProps {
  /**
   * The video to display
   * - **Required** for video content (mutually exclusive with image)
   */
  video?: VideoProps;
  /**
   * Featured image to display
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   * - Only `src` and `alt` properties are required; `width` and `height` are fixed for layout consistency
   */
  image?: Pick<ImageProps, 'src' | 'alt'>;
  /**
   * Whether to show customer logos
   */
  showLogos?: boolean;
  /**
   * Size of the video or image on large screens
   * - `default` spans the full content width
   * - `small` spans the center six columns, keeping the media centered under the hero text
   */
  mediaSize?: 'default' | 'small';
}

const HeroContainer = styled.div`
  padding-block-end: ${({ theme }) => theme.spacing[3]};
  padding-block-start: ${({ theme }) => theme.spacing[4]};
  display: grid;
  ${({ theme }) => theme.grid.columns.small}

  ${HeroMedia} {
    grid-column: 1 / span 2;
    grid-row: 2;
  }

  ${HeroMessaging} {
    grid-column: 1 / span 2;
    grid-row: 1;
  }

  ${HeroSubtitle} {
    font-size: ${({ theme }) => theme.typography.fontSize.superTitle};
    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.superTitle};
    }
  }

  ${HeroLogos} {
    grid-column: 1 / span 2;
    grid-row: 3;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block-end: ${({ theme }) => theme.spacing[4]};
    padding-block-start: ${({ theme }) => theme.spacing[6]};
    align-items: center;
    display: grid;
    ${({ theme }) => theme.grid.columns.large}

    ${HeroMessaging} {
      grid-row: 1;
      grid-column: 2 / span 10;
    }

    ${HeroMedia} {
      grid-row: 2;
      grid-column: 1 / span 12;
    }

    ${HeroLogos} {
      grid-column: 1 / span 12;
      grid-row: 3;
    }

    &.smallMedia ${HeroMedia} {
      grid-column: 4 / span 6;
    }
  }
`;

const HeroCtaContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1.5]};
  justify-content: center;

  margin-block-start: ${({ theme }) => theme.spacing[1.5]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    justify-content: center;
    flex-direction: row;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }

  // Remove the extra padding we get from the p tag
  p {
    margin-block-end: 0;
  }
`;

/**
 *
 * This Hero displays a default or single column style layout for the Hero.
 * Use this Hero when you want the video to be the main focus vs pushing the user down the page.
 *
 */
const HeroDefault = ({
  description,
  video = null,
  headline,
  image = null,
  primaryCta = null,
  secondaryCta = null,
  linkCta = null,
  showLogos = true,
  mediaSize = 'default',
  title,
}: HeroDefaultProps) => {
  return (
    <HeroContainer className={cs({ smallMedia: mediaSize === 'small' })}>
      <HeroMessaging>
        <header>
          <Styled.Eyebrow as="h1">{title}</Styled.Eyebrow>
          <HeroSubtitle aria-roledescription="subtitle">
            {headline}
          </HeroSubtitle>
        </header>
        <HeroDescription description={description} />

        {primaryCta || secondaryCta || linkCta ? (
          <HeroCtaContainer>
            {primaryCta?.href ? (
              <Button
                color="blue500"
                href={primaryCta.href}
                trackingProperty={primaryCta.trackingProperty}
                trackingLabel={primaryCta.trackingLabel}
              >
                {primaryCta.text}
              </Button>
            ) : null}
            {secondaryCta?.href ? (
              <Button
                variant="secondary"
                href={secondaryCta.href}
                trackingProperty={secondaryCta.trackingProperty}
                trackingLabel={secondaryCta.trackingLabel}
              >
                {secondaryCta.text}
              </Button>
            ) : null}
          </HeroCtaContainer>
        ) : null}
        {/* Needs a separate CtaContainer for the linkCta so it can sit outside of the CtaContainer for the primary and secondary ctas */}
        {linkCta ? (
          <HeroCtaContainer>
            <Styled.BodyLg>
              <TextLink cta={linkCta} />
            </Styled.BodyLg>
          </HeroCtaContainer>
        ) : null}
      </HeroMessaging>

      <HeroComputedMedia
        hashedId={video?.hashedId}
        imageAlt={image?.alt}
        imageSrc={image?.src}
        variant={'default'}
        aspect={video?.aspect}
      />
      {showLogos ? <HeroLogos /> : null}
    </HeroContainer>
  );
};

export default HeroDefault;
